
import { GameRole } from '../../types/game';
import { skills } from '../skills';
import { challenges } from '../challenges';

export const technicianRole: GameRole = {
  id: 'technician',
  title: 'Junior Mining Technician',
  description: 'Start your career maintaining and optimizing mining equipment. Learn the technical foundations of Bitcoin mining operations while ensuring maximum uptime and efficiency.',
  difficulty: 'Easy',
  strengths: [
    'Best uptime optimization',
    'Strong technical foundation',
    'Quick early-career advancement'
  ],
  startingSkills: [
    skills.find(s => s.id === 'hardware-maintenance')!
  ],
  careerPath: [
    {
      level: 'Junior',
      title: 'Junior Mining Technician',
      requiredExperience: 0,
      requiredYears: 0
    },
    {
      level: 'Mid-Level',
      title: 'Senior Mining Technician',
      requiredExperience: 150,
      requiredYears: 3
    },
    {
      level: 'Senior',
      title: 'Technical Operations Manager',
      requiredExperience: 350,
      requiredYears: 6
    },
    {
      level: 'Leader',
      title: 'Chief Technical Officer',
      requiredExperience: 700,
      requiredYears: 10
    }
  ],
  specialChallenges: [
    challenges.find(c => c.id === 'cooling-failure')!,
    {
      id: 'technician-equipment-repair',
      title: 'Mining Rig Malfunction',
      description: 'One of the mining rigs has stopped functioning. As the technician on duty, you need to diagnose and fix the issue quickly to minimize downtime.',
      options: [
        {
          text: 'Replace the entire mining rig with a spare unit',
          outcome: 'The mining operation is quickly back online, but using spare equipment increases costs and requires ordering new backup units.',
          kpiEffects: {
            uptime: 3,
            energyCost: 0.005,
            profitMargin: -2
          },
          experienceGain: 15
        },
        {
          text: 'Diagnose and replace only the faulty components',
          outcome: 'You identify the problem as a failing power supply and replace just that component. This takes longer but is more cost-effective.',
          kpiEffects: {
            uptime: 1,
            energyCost: -0.002,
            profitMargin: 1
          },
          experienceGain: 20,
          skillUnlock: skills.find(s => s.id === 'hardware-diagnostics')
        },
        {
          text: 'Create a temporary workaround while waiting for parts',
          outcome: 'Your creative solution gets the rig running at 80% capacity while you wait for replacement parts to arrive.',
          kpiEffects: {
            uptime: 0,
            efficiency: -3,
            profitMargin: 0
          },
          experienceGain: 18
        }
      ],
      difficulty: 2,
      requiredSkills: []
    },
    {
      id: 'technician-cable-management',
      title: 'Cable Management Issues',
      description: 'The mining facility has developed cable management problems leading to cooling inefficiencies. How will you address this issue?',
      options: [
        {
          text: 'Organize and label all cables during overnight maintenance',
          outcome: 'The organized cabling improves airflow and reduces cooling costs. Your thorough documentation makes future maintenance easier.',
          kpiEffects: {
            efficiency: 4,
            energyCost: -0.003,
            uptime: 1
          },
          experienceGain: 25
        },
        {
          text: 'Install cable management trays and routing hardware',
          outcome: 'The investment in proper cable management infrastructure pays off with better cooling and easier maintenance access.',
          kpiEffects: {
            efficiency: 5,
            profitMargin: -1,
            uptime: 2
          },
          experienceGain: 20
        },
        {
          text: 'Quick fix: tie wraps and basic organization of critical areas only',
          outcome: 'Your quick solution helps with immediate cooling issues but doesn\'t solve the underlying organizational problem.',
          kpiEffects: {
            efficiency: 2,
            energyCost: -0.001,
            uptime: 0
          },
          experienceGain: 10
        }
      ],
      difficulty: 1,
      requiredSkills: []
    },
    {
      id: 'technician-firmware-update',
      title: 'Firmware Update Decision',
      description: 'A new firmware update is available for the mining equipment. As the technician, you need to decide how to proceed with the update.',
      options: [
        {
          text: 'Update all equipment immediately during peak hours',
          outcome: 'The update is deployed quickly but causes unexpected downtime during profitable mining hours.',
          kpiEffects: {
            efficiency: 3,
            uptime: -4,
            profitMargin: -2
          },
          experienceGain: 15
        },
        {
          text: 'Schedule the update during off-peak hours with a rollback plan',
          outcome: 'Your careful planning minimizes disruption and includes a backup plan in case of issues. The update is successful.',
          kpiEffects: {
            efficiency: 5,
            uptime: 1,
            profitMargin: 2
          },
          experienceGain: 30,
          skillUnlock: skills.find(s => s.id === 'system-optimization')
        },
        {
          text: 'Test the update on a small batch of miners first',
          outcome: 'Your cautious approach pays off when you discover compatibility issues that would have affected all miners.',
          kpiEffects: {
            efficiency: 2,
            uptime: 3,
            profitMargin: 1
          },
          experienceGain: 25
        }
      ],
      difficulty: 3,
      requiredSkills: []
    },
    // New decision challenges - Adding the 10 new challenges
    {
      id: 'tech-psu-alert',
      title: 'End-of-Life PSU Alert',
      description: 'A large batch of power-supply units is within 5% of its rated lifecycle. What will you do?',
      options: [
        {
          text: 'Proactively schedule full rack swap during off-peak hours',
          outcome: 'Your preventive maintenance approach pays off. All PSUs were replaced without any failures, maintaining optimal mining operations.',
          kpiEffects: {
            uptime: 3,
            efficiency: 2,
            profitMargin: 1
          },
          experienceGain: 25
        },
        {
          text: 'Phase replacements over three months',
          outcome: 'The gradual approach allows for budget spreading but requires more coordination. Most PSUs are replaced before failure.',
          kpiEffects: {
            uptime: 1,
            efficiency: 1
          },
          experienceGain: 15
        },
        {
          text: 'Run PSUs to failure and replace as they die',
          outcome: 'This reactive approach leads to multiple unexpected outages and emergency repairs, causing significant downtime.',
          kpiEffects: {
            uptime: -4,
            efficiency: -2,
            energyCost: 0.001,
            profitMargin: -1
          },
          experienceGain: 5
        }
      ],
      difficulty: 1,
      requiredSkills: []
    },
    {
      id: 'tech-dust-inspection',
      title: 'Dust & Airflow Inspection',
      description: 'Sensors show rising particulate levels in the hot aisle. Your move?',
      options: [
        {
          text: 'Halt one rack tonight for deep cleaning',
          outcome: 'The thorough cleaning removes all accumulated dust and debris, improving airflow and reducing thermal throttling.',
          kpiEffects: {
            uptime: 2,
            efficiency: 2,
            profitMargin: -1
          },
          experienceGain: 20
        },
        {
          text: 'Order filters, clean during next planned downtime',
          outcome: 'This balanced approach minimizes immediate disruption but allows some dust to accumulate in the meantime.',
          kpiEffects: {
            uptime: 1,
            efficiency: 1
          },
          experienceGain: 15
        },
        {
          text: 'Ignore; trust current filters will hold',
          outcome: 'Dust continues to accumulate, eventually causing thermal throttling and reducing mining efficiency.',
          kpiEffects: {
            uptime: -2,
            efficiency: -1,
            profitMargin: 1
          },
          experienceGain: 10
        }
      ],
      difficulty: 1,
      requiredSkills: []
    },
    {
      id: 'tech-cable-chaos',
      title: 'Cable-Management Chaos',
      description: 'Loose harnesses are obstructing airflow, dropping hashrate.',
      options: [
        {
          text: 'Pull an overtime shift to reroute cables neatly',
          outcome: 'Your dedication pays off as the properly organized cables significantly improve airflow and make future maintenance easier.',
          kpiEffects: {
            uptime: 2,
            efficiency: 3
          },
          experienceGain: 30
        },
        {
          text: 'Submit a work-order for next quarter maintenance window',
          outcome: 'The issue is documented and scheduled, but continues to affect performance until the maintenance window arrives.',
          kpiEffects: {
            efficiency: 1
          },
          experienceGain: 15
        },
        {
          text: 'Leave cabling as is; focus on other tasks',
          outcome: 'The airflow restriction continues to negatively impact hash rate and efficiency.',
          kpiEffects: {
            uptime: -1,
            efficiency: -1
          },
          experienceGain: 5
        }
      ],
      difficulty: 1,
      requiredSkills: []
    },
    {
      id: 'tech-firmware-patch',
      title: 'Fleet-Wide Firmware Patch',
      description: 'Vendor releases a patch promising +2% efficiency but warns of possible bricking.',
      options: [
        {
          text: 'Pilot on 10% of miners, monitor 48 hrs, then roll out',
          outcome: 'Your cautious approach identifies a few issues that are resolved before deploying to the full fleet, resulting in improved efficiency.',
          kpiEffects: {
            uptime: -1,
            efficiency: 3,
            profitMargin: 1
          },
          experienceGain: 35,
          skillUnlock: skills.find(s => s.id === 'firmware-management')
        },
        {
          text: 'Wait for community feedback before updating',
          outcome: 'You defer the update until others test it, eventually implementing a safer version with moderate improvements.',
          kpiEffects: {
            efficiency: 1
          },
          experienceGain: 15
        },
        {
          text: 'Skip update—current firmware is "good enough"',
          outcome: 'You avoid potential issues but also miss out on efficiency improvements, putting your operation slightly behind competitors.',
          kpiEffects: {
            profitMargin: -1
          },
          experienceGain: 10
        }
      ],
      difficulty: 2,
      requiredSkills: []
    },
    {
      id: 'tech-offpeak-energy',
      title: 'Off-Peak Energy Contract',
      description: 'Ops offers an off-peak rate if you stagger workloads overnight.',
      options: [
        {
          text: 'Draft shift schedule & pilot 50% fleet off-peak',
          outcome: 'Your comprehensive plan successfully shifts mining load to cheaper rate periods, significantly reducing energy costs.',
          kpiEffects: {
            uptime: -1,
            energyCost: -0.003,
            profitMargin: 3
          },
          experienceGain: 35
        },
        {
          text: 'Negotiate a smaller pilot (20% fleet)',
          outcome: 'The limited pilot shows modest energy savings while minimizing operational changes.',
          kpiEffects: {
            energyCost: -0.001,
            profitMargin: 1
          },
          experienceGain: 15
        },
        {
          text: 'Decline; maintain current 24/7 schedule',
          outcome: 'You maintain consistent uptime but miss the opportunity for energy cost savings.',
          kpiEffects: {
            uptime: 1
          },
          experienceGain: 10
        }
      ],
      difficulty: 2,
      requiredSkills: []
    },
    {
      id: 'tech-edge-sensors',
      title: 'Edge-Sensor Deployment',
      description: 'Low-cost vibration & thermal sensors are available on discount.',
      options: [
        {
          text: 'Install across all racks; integrate with alert system',
          outcome: 'The comprehensive sensor deployment provides early warning of issues, preventing several potential failures.',
          kpiEffects: {
            uptime: 3,
            efficiency: 2,
            profitMargin: 1
          },
          experienceGain: 30
        },
        {
          text: 'Install on problem racks only',
          outcome: 'The targeted approach improves monitoring on known problem areas at a lower cost.',
          kpiEffects: {
            uptime: 1,
            efficiency: 1
          },
          experienceGain: 15
        },
        {
          text: 'Skip sensors; rely on manual checks',
          outcome: 'Manual checks miss early warning signs, resulting in avoidable hardware failures.',
          kpiEffects: {
            uptime: -2
          },
          experienceGain: 5
        }
      ],
      difficulty: 1,
      requiredSkills: []
    },
    {
      id: 'tech-network-latency',
      title: 'Network Latency Spike',
      description: 'Pool latency jumps 80ms, dropping accepted shares.',
      options: [
        {
          text: 'Re-route traffic via new proxy; optimize ports',
          outcome: 'Your technical solution resolves the latency issues, restoring optimal mining performance.',
          kpiEffects: {
            uptime: 2,
            efficiency: 2,
            profitMargin: 1
          },
          experienceGain: 35,
          skillUnlock: skills.find(s => s.id === 'network-optimization')
        },
        {
          text: 'Switch to secondary pool temporarily',
          outcome: 'The pool switch maintains operations but with slightly less favorable terms than your primary pool.',
          kpiEffects: {
            uptime: 1,
            efficiency: 1
          },
          experienceGain: 15
        },
        {
          text: 'Do nothing; assume spike is temporary',
          outcome: 'The latency issues persist longer than expected, causing significant rejected share rates and lost revenue.',
          kpiEffects: {
            uptime: -3,
            efficiency: -1,
            profitMargin: -1
          },
          experienceGain: 5
        }
      ],
      difficulty: 2,
      requiredSkills: []
    },
    {
      id: 'tech-safety-drill',
      title: 'Safety & Fire-Suppression Drill',
      description: 'After a small electrical flare, management asks for a safety plan.',
      options: [
        {
          text: 'Lead a full drill & update SOP (+video training)',
          outcome: 'Your comprehensive safety program improves team readiness and prevents potential incidents.',
          kpiEffects: {
            uptime: 2,
            profitMargin: 1
          },
          experienceGain: 25
        },
        {
          text: 'Conduct basic extinguisher walk-through',
          outcome: 'The minimal training provides basic preparedness but leaves room for improvement.',
          kpiEffects: {
            uptime: 1
          },
          experienceGain: 15
        },
        {
          text: 'File minimal incident report; no action',
          outcome: 'The lack of preparation leaves the team vulnerable to future incidents, potentially causing preventable damage.',
          kpiEffects: {
            uptime: -2,
            profitMargin: -1
          },
          experienceGain: 5
        }
      ],
      difficulty: 1,
      requiredSkills: []
    },
    {
      id: 'tech-rack-retrofit',
      title: 'Quick-Swap Rack Retrofit',
      description: 'Engineering suggests rail kits that cut service time 30%.',
      options: [
        {
          text: 'Champion retrofit; install on all racks',
          outcome: 'The complete retrofit significantly improves maintenance efficiency, reducing downtime and improving serviceability.',
          kpiEffects: {
            uptime: 3,
            efficiency: 2,
            profitMargin: 2
          },
          experienceGain: 30
        },
        {
          text: 'Test retrofit on one row first',
          outcome: 'The limited test proves the concept works, leading to gradual implementation with moderate benefits.',
          kpiEffects: {
            uptime: 1,
            efficiency: 1,
            profitMargin: 1
          },
          experienceGain: 15
        },
        {
          text: 'Reject retrofit; status quo',
          outcome: 'Maintenance continues using the existing slower methods, missing an opportunity for improvement.',
          kpiEffects: {},
          experienceGain: 10
        }
      ],
      difficulty: 2,
      requiredSkills: []
    },
    {
      id: 'tech-immersion-cooling',
      title: 'Immersion-Cooling Pilot',
      description: 'An OEM offers to loan an immersion tank for 10 machines.',
      options: [
        {
          text: 'Accept, run a 6-month A/B study and report results',
          outcome: 'Your thorough testing and documentation proves the value of immersion cooling, leading to significant efficiency improvements.',
          kpiEffects: {
            uptime: 2,
            efficiency: 3,
            energyCost: -0.004,
            profitMargin: 2
          },
          experienceGain: 40,
          skillUnlock: skills.find(s => s.id === 'advanced-cooling')
        },
        {
          text: 'Wait until next budget cycle',
          outcome: 'The delay allows for more industry data to emerge, enabling a more informed decision later.',
          kpiEffects: {
            efficiency: 1,
            energyCost: -0.001,
            profitMargin: 1
          },
          experienceGain: 15
        },
        {
          text: 'Decline; stick with air cooling',
          outcome: 'You maintain the current cooling setup, avoiding potential integration challenges but missing efficiency gains.',
          kpiEffects: {},
          experienceGain: 5
        }
      ],
      difficulty: 2,
      requiredSkills: []
    }
  ],
  careerObjectives: [
    {
      title: 'Uptime Master',
      description: 'Maintain 99% machine uptime for 12 consecutive months',
      target: {
        kpi: 'uptime',
        value: 99
      },
      reward: 'Efficiency Expert Badge'
    },
    {
      title: 'Equipment Optimizer',
      description: 'Achieve 85% equipment efficiency through proper maintenance and optimization',
      target: {
        kpi: 'efficiency',
        value: 85
      },
      reward: 'Technical Excellence Certificate'
    }
  ]
};
